import axios from "axios";
import { LockIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FiLoader, FiX } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
// import { useNavigate } from "react-router";
import { fetchUserDetails } from "../../Redux/ReduxSlices/profileSlice";
import { apiEndpoints } from "../../services/apiConfig";
import { countryData } from "../../utils/CountryCode";
import { validateCouponCode } from "../../Redux/ReduxSlices/paymentSlice";
import { useNavigate } from "react-router-dom";

const PaymentPopup = ({ selectedPlan, setShowModal, currency }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [dateTime, setDateTime] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [couponMessage, setCouponMessage] = useState("");
  const [couponError, setCouponError] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discountedPrice, setDiscountedPrice] = useState(null);
  // const RZP_KEY = import.meta.env.VITE_BACKEND_RAZORPAY_TEST_KEY;
  const RZP_KEY = import.meta.env.VITE_BACKEND_RAZORPAY_LIVE_KEY;

  const { details: userData } = useSelector((state) => state.profile);
  const {
    register,
    handleSubmit,
    getValues,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (currency === "INR") {
      setValue("countryCode", "+91");
    } else {
      setValue("countryCode", "");
    }
  }, [currency]);

  useEffect(() => {
    dispatch(fetchUserDetails());
  }, [dispatch]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setDateTime(new Date());
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const loadRazorpayScript = () => {
      const script = document.createElement("script");
      script.src = apiEndpoints.razorpayCheckout;
      script.async = true;
      document.body.appendChild(script);
    };
    loadRazorpayScript();
  }, []);

  useEffect(() => {
    if (paymentStatus) {
      const timer = setTimeout(() => setPaymentStatus(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [paymentStatus]);

  const formatCurrency = (amount) => {
    const amountToFormat = discountedPrice !== null ? discountedPrice : amount;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amountToFormat);
  };

  function getCurrentDateTime() {
    const now = new Date();

    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");

    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  const selectedPrice = selectedPlan?.smalles_unit_price;
  const amountInINR = selectedPrice;
  const amountInUSD = parseFloat(selectedPlan.price);
  const amountInPaise = amountInINR * 100;
  const amountInCents = amountInUSD * 100;

  // Use discounted price if available
  const finalAmountForPayment =
    discountedPrice !== null
      ? currency == "INR"
        ? discountedPrice * 100
        : discountedPrice * 100
      : currency == "INR"
        ? amountInPaise
        : amountInCents;

  const currentDateTime = getCurrentDateTime();

  const handleFailure = async (errorPayload) => {
    const token = localStorage.getItem("token");

    try {
      await axios.post(apiEndpoints.failedPayment, errorPayload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (reportError) {
      // console.error("Error reporting failed:", reportError);
    }
  };

  const handleSendPaymentDetails = async (response) => {
    setIsLoading(true);
    const cardHolderName = watch("cardholderName");
    const token = localStorage.getItem("token");

    const finalPayload = {
      company_id: localStorage.getItem("company_id"),
      razorpay_payment_id: response.id,
      razorpay_order_id: response.order_id,
      entity: response.entity,
      amount: response.amount,
      currency: response.currency,
      status: response.status,
      invoice_id: response.invoice_id,
      international: response.international,
      method: response.method,
      amount_refunded: response.amount_refunded,
      refund_status: response.refund_status,
      captured: response.captured,
      description: response.description,
      card_id: response.card_id,
      bank: response.bank,
      wallet: response.wallet,
      vpa: response.vpa,
      email: response.email,
      contact: response.contact,
      notes: { username: response.notes.username ?? "unknown username" },
      fee: response.fee,
      tax: response.tax,
      error_code: response.error_code,
      error_description: response.error_description,
      error_source: response.error_source,
      error_step: response.error_step,
      error_reason: response.error_reason,
      acquirer_data: response.acquirer_data,
      rayzorpay_unix_timestamp: response.created_at,
      upi: response.upi,
      card: response.card,
    };

    setIsLoading(true);

    try {
      await axios.post(apiEndpoints.sendPaymentDetails, finalPayload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      setPaymentStatus("success");

      setTimeout(() => {
        localStorage.removeItem("payment_modal_closed_at");
        localStorage.removeItem("payment_modal_close_count");
        closeModal();
        sessionStorage.setItem("alertSuccessRedirected", "true");
        navigate("/payment-success", { state: response });
      }, 1000);
      setIsLoading(false);
    } catch (error) {
      // console.error("Error sending payment data:", error);

      const errorPayload = {
        amount: response.amount,
        contact: response.contact,
        email: response.email,
        razorpay_order_id: response.id,
        date_time: currentDateTime ?? "2025-11-10 10:00:00",
        level: "4",
        problem: error.message,
      };

      await handleFailure(errorPayload);
      setPaymentStatus("failure");
      navigate("/payment-failure");
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSuccess = async (response) => {
    const token = localStorage.getItem("token");
    const payload = {
      payment_id: response.razorpay_payment_id,
      razorpay_order_id: response.razorpay_order_id,
      subscription_pk: selectedPlan.subscription_pk,
      coupon_code: appliedCoupon ? appliedCoupon.code : null,
    };

    try {
      setIsLoading(true);
      await axios
        .post(apiEndpoints.confirmPayment, payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        })
        .then(async (res) => {
          if (res.data.success) {
            navigate("/payment-success");
          } else {
            navigate("/payment-failure");
          }
        });
      setIsLoading(false);
    } catch (error) {
      // console.error("Error sending payment data:", error);

      const phone = watch("phoneNumber");
      const email = watch("email");

      const errorPayload = {
        amount: finalAmountForPayment,
        contact: phone,
        email: email,
        razorpay_order_id: response.razorpay_payment_id,
        date_time: currentDateTime ?? "2025-11-10 10:00:00",
        level: "3",
        problem: error.message,
      };

      await handleFailure(errorPayload);
      setPaymentStatus("failure");
      navigate("/payment-failure");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRazorPayOrder = async (c) => {
    const token = localStorage.getItem("token");
    closeModal();
    const payload = {
      // amount: discountedPrice !== null ? discountedPrice * 100 : selectedPrice,
      subscription_pk: selectedPlan.subscription_pk,
      coupon_code: appliedCoupon ? appliedCoupon.code : null,
      currency: "INR",
      notes: {
        plan: "starter",
        user_hint: "support@maxipitch.com",
      },
    };
    // console.log("pppp", payload);
    try {
      await axios
        .post(apiEndpoints.createOrder, payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        })
        .then(async (data) => {
          await handlePayment(data);
        });
    } catch (error) {
      // console.error("Error sending payment data:", error);

      const phone = watch("phoneNumber");
      const email = watch("email");

      const errorPayload = {
        amount: finalAmountForPayment,
        contact: phone,
        email: email,
        razorpay_order_id: "",
        date_time: currentDateTime ?? "2025-11-10 10:00:00",
        level: "1",
        problem: error.message,
      };

      await handleFailure(errorPayload);
      setPaymentStatus("failure");
      navigate("/payment-failure");
    }
  };

  const handlePayment = async (e) => {
    if (!e.data.data || !e.data.data.id) {
      return;
    }

    var rzId = e.data.data.id;
    const name = watch("cardholderName");
    const phone = watch("phoneNumber");
    const email = watch("email");

    const nameRegex = /^[a-zA-Z\s]+$/;
    if (!name || !nameRegex.test(name)) {
      return;
    }

    const phoneRegex = /^[0-9]{10}$/;
    if (!phone || !phoneRegex.test(phone)) {
      return;
    }

    const emailRegex = /\S+@\S+\.\S+/;
    if (!email || !emailRegex.test(email)) {
      return;
    }

    const options = {
      key: RZP_KEY,
      amount: finalAmountForPayment,
      currency: currency,
      order_id: rzId,
      name: "Digikore Studio Limited",
      description: "Transaction from Maxipitch",
      handler: async function (response) {
        await handlePaymentSuccess(response);
      },
      prefill: {
        name: name,
        email: email,
        contact: phone,
      },
      theme: {
        color: "#61b1a7",
      },
      modal: {
        ondismiss: async function () {
          const errorPayload = {
            amount: finalAmountForPayment,
            contact: phone,
            email: email,
            razorpay_order_id: rzId,
            date_time: currentDateTime ?? "2025-11-10 10:00:00",
            level: "2",
            problem: "Error in Razorpay SDK",
          };

          await handleFailure(errorPayload);
          setPaymentStatus("failure");
          navigate("/payment-failure");
        },
      },
    };

    const rzp1 = new window.Razorpay(options);
    rzp1.open();
  };

  const validateCoupon = async (couponCode) => {
    if (!couponCode || couponCode.trim() === "") {
      setCouponMessage("Please enter a coupon code");
      setCouponError(true);
      setTimeout(() => {
        setCouponMessage("");
        setCouponError(false);
      }, 3000);
      return;
    }

    setIsApplyingCoupon(true);
    setCouponMessage("");
    setCouponError(false);

    const token = localStorage.getItem("token");
    try {
      const payload = {
        subscription_pk: selectedPlan.subscription_pk,
        coupon_code: couponCode,
        currency: currency,
        // code: couponCode,
        // amount: selectedPlan.price,
      };
      const response = await dispatch(validateCouponCode(payload)).unwrap();

      if (response.success) {
        // Calculate discounted price in original currency units
        const finalAmountMinor = response.data.final_amount_minor;
        let discountedAmount;

        if (currency === "INR") {
          discountedAmount = finalAmountMinor / 100;
        } else {
          // Convert INR to USD (assuming conversion rate, adjust as needed)
          discountedAmount = finalAmountMinor / 100 / 83; // Example conversion rate
        }

        setDiscountedPrice(discountedAmount);
        setAppliedCoupon({
          code: couponCode,
          discount: response.data.discount_amount_minor / 100,
          originalAmount: selectedPlan.price,
        });
        setCouponMessage(
          `Coupon applied! You saved ${formatCurrency(response.data.discount_amount_minor / 100)}`,
        );
        setCouponError(false);

        // Clear message after 3 seconds
        setTimeout(() => {
          setCouponMessage("");
        }, 3000);
      } else {
        setCouponMessage(response.message || "Invalid coupon code");
        setCouponError(true);
        // setTimeout(() => {
        //   setCouponMessage("");
        //   setCouponError(false);
        // }, 3000);
      }
    } catch (error) {
      // console.error("Error validating coupon:", error);
      setCouponMessage(
        error.response?.data?.message || "Error applying coupon",
      );
      setCouponError(true);
      // setTimeout(() => {
      //   setCouponMessage("");
      //   setCouponError(false);
      // }, 3000);
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setDiscountedPrice(null);
    setCouponMessage("Coupon removed");
    setCouponError(false);
    setValue("couponCode", "");

    setTimeout(() => {
      setCouponMessage("");
    }, 3000);
  };

  const closeModal = () => {
    setPaymentStatus(null);
    setShowModal(false);
  };

  const onSubmit = (data) => {
    setIsLoading(true);
    handleRazorPayOrder();
  };

  const displayPrice =
    discountedPrice !== null ? discountedPrice : selectedPlan.price;

  return (
    <div className="relative w-full">
      {/* Loader overlay INSIDE DialogContent */}
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-75">
          <FiLoader className="animate-spin text-[#822bda] text-4xl" />
        </div>
      )}

      {/* Header (normal, not sticky) */}
      <div className="border-b border-gray-200">
        <div className="relative flex items-center py-3 sm:py-4 px-2 sm:px-8">
          <h1 className="w-full text-lg sm:text-2xl font-bold text-black pr-10">
            Complete Your Purchase
          </h1>
        </div>
      </div>

      {/* Body (no modal scroll rules; DialogContent can control height/scroll) */}
      <div className="flex flex-col md:flex-row">
        {/* Left Column - Plan Details */}
        <div className="w-full md:w-2/5 p-4 sm:p-6  md:border-r border-gray-200 border-b md:border-b-0">
          <div className="mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-bold text-black">
              {selectedPlan.name}
            </h2>
            <p className="text-sm sm:text-md font-semibold text-gray-800 mt-1 sm:mt-2">
              {formatCurrency(displayPrice)}
              {currency === "USD" && "/month"}
            </p>
            <p className="text-gray-600 mt-1 text-xs sm:text-sm">
              50 emails per day
            </p>
          </div>

          <div className="border-t border-gray-300 pt-3 sm:pt-4 mt-3 sm:mt-4">
            <div className="flex justify-between items-center py-2">
              <span className="text-gray-700 text-sm">Subtotal</span>
              <span className="text-black font-medium text-sm">
                {formatCurrency(selectedPlan.price)}
              </span>
            </div>
            {/* {appliedCoupon && (
              <div className="flex justify-between items-center py-2">
                <span className="text-green-600 text-sm">
                  Discount ({appliedCoupon.code})
                </span>
                <span className="text-green-600 font-medium text-sm">
                  -{formatCurrency(appliedCoupon.discount)}
                </span>
              </div>
            )} */}
            {discountedPrice !== null && (
              <div className="flex justify-between items-center py-2 border-t border-gray-200 mt-2 pt-2">
                <span className="text-black font-semibold text-sm">Total</span>
                <span className="text-black font-bold text-sm">
                  {formatCurrency(discountedPrice)}
                </span>
              </div>
            )}
          </div>

          <div className="mt-4 sm:mt-8 space-y-2 sm:space-y-4 text-xs sm:text-sm text-gray-700">
            <div className="flex items-center font-semibold text-black mb-3 sm:mb-4">
              <img
                src="https://razorpay.com/favicon.png"
                alt="Razorpay"
                className="w-5 h-5 sm:w-6 sm:h-6 mr-2"
              />
              <span>Secure Payment</span>
            </div>

            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <LockIcon className="w-4 h-4 mt-0.5 shrink-0" />
                <span>256-bit SSL Encrypted Secure Checkout</span>
              </div>

              <div className="flex items-start gap-2">
                <LockIcon className="w-4 h-4 mt-0.5 shrink-0" />
                <span>PCI-DSS Compliant Payment Gateway</span>
              </div>

              <div className="flex items-start gap-2">
                <LockIcon className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Safe & Trusted Transactions</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Payment Form */}
        <div className="w-full md:w-3/5 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 sm:mb-6">
            Payment Information
          </h3>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Customer Name */}
            <div>
              <label
                htmlFor="cardholderName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Customer Name <span className="text-red-500">*</span>
              </label>
              <input
                id="cardholderName"
                placeholder="Enter your name"
                type="text"
                {...register("cardholderName", {
                  required: "Name is required",
                  minLength: {
                    value: 2,
                    message: "Name must be at least 2 characters",
                  },
                  pattern: {
                    value: /^[A-Za-z\s]+$/,
                    message: "Only letters and spaces allowed",
                  },
                })}
                className="w-full p-3 max-md:p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-black transition-all"
              />
              {errors.cardholderName && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.cardholderName.message}
                </p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <label
                htmlFor="phoneNumber"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Phone Number <span className="text-red-500">*</span>
              </label>

              <div className="flex sm:flex-row gap-2">
                <div className="relative w-full sm:w-1/3">
                  <select
                    {...register("countryCode", {
                      required: "Country code is required",
                    })}
                    className="w-full p-3 max-md:p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-black transition-all appearance-none bg-white"
                    defaultValue="+91"
                  >
                    <option value={""}>Select</option>
                    {countryData.map((country, i) => (
                      <option key={i} value={country.code}>
                        {country.code} {country.name}
                      </option>
                    ))}
                  </select>

                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      ></path>
                    </svg>
                  </div>
                </div>

                <div className="w-full sm:flex-1">
                  <input
                    id="phoneNumber"
                    type="tel"
                    placeholder="Enter your phone number"
                    {...register("phoneNumber", {
                      required: "Phone number is required",
                      pattern: {
                        value: /^[0-9]{10,15}$/,
                        message: "Enter a valid phone number (10-15 digits)",
                      },
                    })}
                    className="w-full p-3 max-md:p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-black transition-all"
                  />
                </div>
              </div>

              {errors.phoneNumber && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.phoneNumber.message}
                </p>
              )}
              {errors.countryCode && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.countryCode.message}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                placeholder="Enter your email"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Enter a valid email address",
                  },
                })}
                className="w-full p-3 max-md:p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-black transition-all"
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Coupon Code */}
            <div>
              <label
                htmlFor="couponCode"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Coupon Code (if any)
              </label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <input
                    id="couponCode"
                    type="text"
                    placeholder="Enter coupon code"
                    {...register("couponCode", {
                      onChange: (e) => {
                        e.target.value = e.target.value.toUpperCase();
                      },
                    })}
                    disabled={appliedCoupon !== null}
                    className={`w-full p-3 max-md:p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-black transition-all ${
                      appliedCoupon ? "bg-gray-100 text-gray-500" : ""
                    }`}
                  />
                  {appliedCoupon && (
                    <button
                      type="button"
                      onClick={removeCoupon}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <FiX className="w-5 h-5" />
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => validateCoupon(getValues("couponCode"))}
                  disabled={isApplyingCoupon || appliedCoupon !== null}
                  className="px-6 py-3 max-md:px-4 max-md:py-2 bg-gray-800 text-white rounded-md font-medium hover:bg-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isApplyingCoupon ? (
                    <>
                      <FiLoader className="animate-spin text-white" />
                      <span>Applying...</span>
                    </>
                  ) : (
                    "Apply"
                  )}
                </button>
              </div>
              {couponMessage && (
                <p
                  className={`text-sm mt-1 ${couponError ? "text-red-500" : "text-green-500"}`}
                >
                  {couponMessage}
                </p>
              )}
            </div>

            {/* Submit */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full flex justify-center items-center gap-2 py-3 px-4 rounded-md shadow-md font-semibold transition-all
                ${
                  isLoading
                    ? "bg-gray-500 cursor-not-allowed"
                    : "main-button hover:bg-gray-800 text-white"
                }`}
              >
                {isLoading ? (
                  <>
                    <FiLoader className="animate-spin text-white text-lg" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <span>Pay {formatCurrency(displayPrice)}</span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PaymentPopup;
